// Design tokens shared by every scene and widget. Colours match the
// generated art (see docs/superpowers/plans/2026-09-03-nanocars-v1.md,
// "Asset status"); Task 15 may retune these once the final art pass lands.

export const COLORS = {
  background: 0x3b2a1e, // dark wood table
  panel: 0xf6e7c8, // cream panel
  outline: 0x4a2c17, // brown outline
  accentRed: 0xe3342f,
  accentYellow: 0xf7c12d,
  textDark: 0x2b1a10,
  textLight: 0xfff4dc,
  // Fallback fill for a colour index that has no matching PLAYER_COLORS
  // entry. Deliberately a plain grey, never black/0x000000 - `colour: 0` is
  // a *valid* slot index (red), so any fallback path must never collapse to
  // the same value a missing/garbage index would produce by accident.
  neutralGrey: 0x9a9a9a,
} as const;

// Player slot colours, in slot order (slot 0 = red, ...). LobbyPlayer.colour
// (see @nanocars/shared net/messages.ts) is an INDEX into this array, not a
// colour value itself - the server only ever stores/validates 0..COLOUR_COUNT-1.
export const PLAYER_COLORS: readonly number[] = [
  0xe3342f, // red
  0x2f6fe3, // blue
  0xf7c12d, // yellow
  0x3fb64a, // green
  0x8e4bd6, // purple
  0xf08a1f, // orange
];

// Maps a LobbyPlayer.colour slot index to its display fill colour. Index 0
// is a legitimate slot (red) so this must not use `||` or treat 0 as
// missing; an out-of-range/NaN index falls back to COLORS.neutralGrey.
export function colourForSlot(index: number): number {
  return PLAYER_COLORS[index] ?? COLORS.neutralGrey;
}

export const FONT_FAMILY = "'Nunito', 'Trebuchet MS', sans-serif";

// Nudges a colour towards white by `amount` (0..1). Used for hover states so
// interactive widgets change fill colour only, never position (no hover
// "lift").
export function lighten(color: number, amount = 0.12): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return (mix(r) << 16) | (mix(g) << 8) | mix(b);
}

// Nudges a colour towards black by `amount` (0..1). Used for pressed states.
export function darken(color: number, amount = 0.12): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const mix = (c: number) => Math.round(c * (1 - amount));
  return (mix(r) << 16) | (mix(g) << 8) | mix(b);
}
